import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  FormSelect,
  FormCheckbox,
  FormTextArea,
  FormInput,
  FormRadioButton,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldDate,
  getFieldValue,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import { getProgressNotes } from "../services/soar";
import generalOptionSets from "../../options/general.json";

function KY1294B() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [progressNotes, setProgressNotes] = useState([]);
  const [progressNotesLoadStatus, setProgressNotesLoadStatus] = useState({});

  useEffect(() => {
    if (progressNotesLoadStatus.loaded || progressNotesLoadStatus.loading) {
      return;
    }

    const loadProgressNotes = async () => {
      setProgressNotesLoadStatus({ loading: true });
      const response = await getProgressNotes(clientid);

      if (!isMountedRef.current) {
        return;
      }

      setProgressNotes(response);
      setProgressNotesLoadStatus({ loaded: true });
    };

    loadProgressNotes();
  }, [progressNotesLoadStatus, clientid]);

  const handleMonthEndingChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });
    const momentObj = moment();

    if (Number(getFieldValue(data.MonthEnding) > momentObj.month() + 1)) {
      momentObj.subtract(1, "year");
    }

    momentObj.month(input.value - 1);

    const startOfMonth = momentObj
      .startOf("month")
      .format(moment.HTML5_FMT.DATE);
    const endOfMonth = momentObj.endOf("month").format(moment.HTML5_FMT.DATE);

    const monthlyProgressNotes = progressNotes.filter((progressNote) =>
      moment(getFieldDate(progressNote["rs.ccx_starttime"])).isBetween(
        startOfMonth,
        endOfMonth,
        undefined,
        "[]"
      )
    );

    if (monthlyProgressNotes.length === 0) {
      return;
    }

    function onlyUnique(value, index, array) {
      return array.indexOf(value) === index;
    }

    const monthlyCAPNotes = monthlyProgressNotes.filter(
      (progressNote) =>
        getFieldName(progressNote["rs.ccx_serviceitem"]) === "CAP"
    );
    const monthlyNonMedNotes = monthlyProgressNotes.filter(
      (progressNote) =>
        getFieldName(progressNote["rs.ccx_serviceitem"]) === "Non-Med DSTIR"
    );
    const monthlyCAPProviders = monthlyCAPNotes
      .map((progressNote) => getFieldName(progressNote["rs.ccx_caregiver"]))
      .filter(onlyUnique)
      .sort();
    const monthlyCAPDates = monthlyCAPNotes
      .map((progressNote) => getFieldDate(progressNote["rs.ccx_starttime"]))
      .filter(onlyUnique)
      .sort((a, b) => {
        const aDate = moment(a);
        const bDate = moment(b);

        if (aDate.isSameOrBefore(bDate)) {
          return 1;
        } else if (aDate.isSameOrAfter(bDate)) {
          return -1;
        } else {
          return 0;
        }
      });
    const monthlyNonMedProviders = monthlyNonMedNotes
      .map((progressNote) => getFieldName(progressNote["rs.ccx_caregiver"]))
      .filter(onlyUnique)
      .sort();
    const monthlyNonMedDates = monthlyNonMedNotes
      .map((progressNote) => getFieldDate(progressNote["rs.ccx_starttime"]))
      .filter(onlyUnique)
      .sort((a, b) => {
        const aDate = moment(a);
        const bDate = moment(b);

        if (aDate.isSameOrBefore(bDate)) {
          return 1;
        } else if (aDate.isSameOrAfter(bDate)) {
          return -1;
        } else {
          return 0;
        }
      });

    const newCAPRow = {
      Date: monthlyCAPDates.join(`
`),
      Provider: monthlyCAPProviders.join(`
`),
      Description:
        "Case management provided through phone calls and face-to-face contacts.",
    };

    const existingCAPRows = JSON.parse(getFieldValue(data.TreatmentPlan));
    existingCAPRows.unshift(newCAPRow);

    const newNonMedRow = {
      Date: monthlyNonMedDates.join(`
`),
      Provider: monthlyNonMedProviders.join(`
`),
    };

    const existingNonMedRows = JSON.parse(getFieldValue(data.Therapy));
    existingNonMedRows.unshift(newNonMedRow);

    obj.TreatmentPlan["@value"] = JSON.stringify(existingCAPRows);
    obj.Therapy["@value"] = JSON.stringify(existingNonMedRows);
    setData(obj);
  };

  const getDivision = useCallback(() => {
    if (
      !getFieldValue(data.SelectedProgram) ||
      !clientData?.programs?.length ||
      clientData.programs.length === 0
    ) {
      return "";
    }

    const selectedProgramInfo = clientData.programOptions?.filter(
      (p) => p._id === getFieldValue(data.SelectedProgram)
    )[0];

    if (!selectedProgramInfo) {
      return "";
    }

    return selectedProgramInfo.name.split(" - ")[0];
  }, [data, clientData]);

  const handleDateOfApprovalChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    const dateOfApproval = moment(input.value);

    obj.DateOfReview["@value"] = dateOfApproval
      .add(6, "M")
      .format(moment.HTML5_FMT.DATE);
    setData(obj);

    const errs = { ...errors };
    delete errs.DateOfReview;
    setErrors(errs);
  };

  const getMonthEnding = (dataObj) => {
    if (!getFieldValue(dataObj.MonthEnding)) {
      return;
    }

    const momentObj = moment();

    if (Number(getFieldValue(dataObj.MonthEnding) > momentObj.month() + 1)) {
      momentObj.subtract(1, "year");
    }

    momentObj.month(dataObj.MonthEnding["@value"] - 1);
    return momentObj.format("MMMM YYYY");
  };

  const overallGoalsOptions = [
    {
      _id: "DPP-1293 in development",
      name: "DPP-1293 in development",
    },
    {
      _id: "Remains the same as described in the rehabilitative services plan of care, DPP-1293",
      name: "Remains the same as described in the rehabilitative services plan of care, DPP-1293",
    },
    {
      _id: "Have been changed as indicated on the attached revised DPP-1293",
      name: "Have been changed as indicated on the attached revised DPP-1293",
    },
  ];

  ////UI
  return (
    <div style={{ fontFamily: "Segoe UI" }}>
      <div className="clearfix">
        <div className="float-start mb-2">
          <BackIcon />
        </div>
        <div className="float-end mb-2">
          <a
            href="https://safy365.sharepoint.com/:w:/s/Kentucky/EXsclM3AiChLkMyNTjKoSYQBQBCo2jc_chxJ-OqQfwNVgg"
            target="_blank"
            rel="noreferrer"
          >
            Forms Information
          </a>
        </div>
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          hideHeaderOnPrint={true}
          letterhead={
            <div className="w-100 text-center fw-bold">
              DPP-1294B
              <br />
              R. 6/2022
              <br />
              Commonwealth of Kentucky
              <br />
              Cabinet for Health and Family Services
              <br />
              Department of Community Based Services
              <br />
              Division of Protection and Permanency
              <br />
              <br />
              Rehabilitative Services Monthly Progress Report
            </div>
          }
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          printStyle={`body {
            font-family: "Segoe UI";
            font-size: .75rem;
          }`}
        />
        <div className="py-2 border-bottom border-dark d-print-none">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                This form must be completed and sent to the DCBS worker by the
                15th of each month.
              </p>
              <div className="row">
                <div className="col-6">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">MONTH ENDING:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormSelect
                          name="MonthEnding"
                          error={errors.MonthEnding}
                          value={data.MonthEnding["@value"]}
                          options={generalOptionSets.month}
                          onChange={handleMonthEndingChange}
                          identical={compare("MonthEnding")}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled || !progressNotesLoadStatus.loaded
                          }
                          divClassName="w-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-7">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">DCBS CASE MANAGER:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="CaseManager"
                          error={errors.CaseManager}
                          value={data.CaseManager["@value"]}
                          onChange={handleChange}
                          identical={compare("CaseManager")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="d-flex lh-12 w-auto">
                    <span className="me-2">CHILD NAME:</span>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        {clientData &&
                          clientData.client &&
                          clientData.client.fullName}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-5">
                  <div className="d-flex lh-12 w-auto">
                    <span className="me-2">DOB:</span>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        {clientData &&
                          clientData.client &&
                          getFieldDate(clientData.client.dob)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="d-flex lh-12 mt-3 w-auto">
                    <div className="me-2">SSN NUMBER:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="SSNumber"
                          error={errors.SSNumber}
                          value={data.SSNumber["@value"]}
                          onChange={handleChange}
                          identical={compare("SSNumber")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-7">
                  <div className="d-flex lh-12 mt-3 w-auto">
                    <div className="me-2">PROVIDER/FACILITY:</div>
                    <div className="flex-fill">
                      <div className="w-100 ps-1 border-bottom border-dark">
                        SAFY of {getDivision(data)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col-7">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">
                      Date of Current DPP-1293 Approval:
                    </div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="DateOfApproval"
                          error={errors.DateOfApproval}
                          value={data.DateOfApproval["@value"]}
                          onChange={handleDateOfApprovalChange}
                          identical={compare("DateOfApproval")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-7">
                  <div className="d-flex lh-12 w-auto">
                    <div className="me-2">Date of Next Six Month Review:</div>
                    <div className="flex-fill">
                      <div className="w-100">
                        <FormInput
                          name="DateOfReview"
                          error={errors.DateOfReview}
                          value={data.DateOfReview["@value"]}
                          onChange={handleChange}
                          identical={compare("DateOfReview")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          divClassName="w-100 border-bottom border-dark"
                          printClassName="ps-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <h6>
                <b>
                  <u>TREATMENT SUMMARY:</u>
                </b>
                <br />
                OVERALL GOALS/OBJECTIVES OF REHABILITATIVE SERVICES PLAN:
              </h6>
              <FormRadioButton
                name="OverallGoals"
                error={errors.OverallGoals}
                value={data.OverallGoals["@value"]}
                options={overallGoalsOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                optionsOnNewLine={true}
              />
              <div className="d-flex mt-2">
                <span className="me-2">PROGRESS NOTES</span>
                <span className="d-print-none">
                  (
                  <Link
                    to={`/client/${clientid}?tab=documentation`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-print-none"
                  >
                    Documentation
                  </Link>
                  ):
                </span>
              </div>
              <ol>
                <li>
                  <span className="h6 me-2">
                    <b>
                      <u>TREATMENT PLANNING AND SUPPORT -</u>
                    </b>
                  </span>
                  <span>
                    Describe representative treatment planning and support
                    activities performed over the last month in support of the
                    goals and objectives of the rehabilitative services plan of
                    care:
                  </span>
                  <FieldTable
                    name="TreatmentPlan"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2 mt-4">
                      <div className="row border-bottom">
                        <div className="col-2 border-end py-2">DATE</div>
                        <div className="col-4 border-end py-2">PROVIDER</div>
                        <div className="col py-2">ACTIVITY DESCRIPTION</div>
                      </div>
                      {data.TreatmentPlan["@value"] &&
                        JSON.parse(data.TreatmentPlan["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-2 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "TreatmentPlan");
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(index, "TreatmentPlan");
                                      }}
                                    ></i>
                                    <FormInput
                                      name="Date"
                                      value={item.Date}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TreatmentPlan"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    {!formDisabled && (
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(index, "TreatmentPlan");
                                        }}
                                      ></i>
                                    )}
                                    {item.Date}
                                  </>
                                )}
                              </div>
                              <div className="col-4 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Provider"
                                    value={item.Provider}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentPlan"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Provider
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Description"
                                    value={item.Description}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentPlan"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Description
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("TreatmentPlan", { IsEditable: true });
                        }}
                      >
                        Add
                      </button>
                    ) : null}
                    {errors.TreatmentPlan && (
                      <div className="alert alert-danger">
                        {errors.TreatmentPlan}
                      </div>
                    )}
                  </FieldTable>
                  <br />
                  <br />
                </li>
                <li>
                  <span className="h6 me-2">
                    <b>
                      <u>LIVING SKILLS DEVELOPMENT -</u>
                    </b>
                  </span>
                  <span>
                    Describe representative skills training and development
                    activities performed over the last month in support of the
                    goals and objectives of the rehabilitative services plan of
                    care:
                  </span>
                  <FieldTable
                    name="LivingSkills"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2 mt-4">
                      <div className="row border-bottom">
                        <div className="col-2 border-end py-2">DATE</div>
                        <div className="col-4 border-end py-2">PROVIDER</div>
                        <div className="col py-2">ACTIVITY DESCRIPTION</div>
                      </div>
                      {data.LivingSkills["@value"] &&
                        JSON.parse(data.LivingSkills["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-2 border-end py-2">
                                {getMonthEnding(data)}
                              </div>
                              <div className="col-4 border-end py-2">SAFY</div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Description"
                                    value={item.Description}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "LivingSkills");
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Description
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("LivingSkills", { IsEditable: true });
                        }}
                      >
                        Add
                      </button>
                    ) : null}
                    {errors.LivingSkills && (
                      <div className="alert alert-danger">
                        {errors.LivingSkills}
                      </div>
                    )}
                  </FieldTable>
                  <br />
                  <br />
                </li>
                <li>
                  <span className="h6 me-2">
                    <b>
                      <u>THERAPY, EVALUATION, AND ASSESSMENT -</u>
                    </b>
                  </span>
                  <span>
                    Describe representative treatment planning and support
                    activities performed over the last month in support of the
                    goals and objectives of the rehabilitative services plan of
                    care:
                  </span>
                  <FieldTable name="Therapy" commentsHandler={handleComments}>
                    <div className="container border-top border-start border-end mb-2 mt-4">
                      <div className="row border-bottom">
                        <div className="col-2 border-end py-2">DATE</div>
                        <div className="col-4 border-end py-2">PROVIDER</div>
                        <div className="col py-2">ACTIVITY DESCRIPTION</div>
                      </div>
                      {data.Therapy["@value"] &&
                        JSON.parse(data.Therapy["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-2 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "Therapy");
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(index, "Therapy");
                                      }}
                                    ></i>
                                    <FormInput
                                      name="Date"
                                      value={item.Date}
                                      onChange={(e) => {
                                        handleRowChange(e, index, "Therapy");
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    {!formDisabled && (
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(index, "Therapy");
                                        }}
                                      ></i>
                                    )}
                                    {item.Date}
                                  </>
                                )}
                              </div>
                              <div className="col-4 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Provider"
                                    value={item.Provider}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "Therapy");
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Provider
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Description"
                                    value={item.Description}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "Therapy");
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Description
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("Therapy", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add
                      </button>
                    ) : null}
                    {errors.Therapy && (
                      <div className="alert alert-danger">{errors.Therapy}</div>
                    )}
                  </FieldTable>
                </li>
              </ol>
              <br />
              <b>
                <u>CASE STATUS SUMMARY</u>
              </b>
              <br />
              <ol>
                <li>
                  <FormTextArea
                    name="Adjustment"
                    label="SUMMARIZE Child's/YOUTH’S ADJUSTMENT TO FACILITY:"
                    error={errors.Adjustment}
                    value={data.Adjustment["@value"]}
                    onChange={handleChange}
                    identical={compare("Adjustment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  <FormTextArea
                    name="Services"
                    label="SERVICES PROVIDED TO CHILD/YOUTH AND Child's/YOUTH’S FAMILY:"
                    error={errors.Services}
                    value={data.Services["@value"]}
                    onChange={handleChange}
                    identical={compare("Services")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  <FormTextArea
                    name="ReturnProgress"
                    label="PROGRESS TOWARD RETURN OF CHILD/YOUTH TO THE HOME OR COMMUNITY (IF APPLICABLE):"
                    error={errors.ReturnProgress}
                    value={data.ReturnProgress["@value"]}
                    onChange={handleChange}
                    identical={compare("ReturnProgress")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  <FormTextArea
                    name="PermanencyGoal"
                    label="PERMANENCY GOAL FOR CHILD/YOUTH:"
                    error={errors.PermanencyGoal}
                    value={data.PermanencyGoal["@value"]}
                    onChange={handleChange}
                    identical={compare("PermanencyGoal")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  YOUTH ON EXTENDED COMMITMENT:
                  <br />
                  <br />
                  <ol type="A">
                    <li>
                      <p>
                        Please check all that apply and include verification
                        documentation (class schedule, unofficial transcript,
                        paycheck stubs, documentation of medical condition,
                        etc.)
                      </p>
                      <FormCheckbox
                        name="EmployedFullTime"
                        label="Employed full-time (working at least 30 hours per week)"
                        checked={data.EmployedFullTime["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="EnrolledFullTime"
                        label="Enrolled full-time in an educational program"
                        checked={data.EnrolledFullTime["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="EmployedEnrolledPartTime"
                        label="Employed part-time and enrolled in part-time in an educational program"
                        checked={
                          data.EmployedEnrolledPartTime["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="HighSchool"
                        label="Attending high school or a program leading to a high school diploma or a high school equivalency certificate (GED)"
                        checked={data.HighSchool["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="College"
                        label="Attending college or vocational program"
                        checked={data.College["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="JobBarriers"
                        label="Participating in a program or activity that promotes or removes barriers to employment"
                        checked={data.JobBarriers["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="Incapable"
                        label="Is incapable of doing any of the above due to a documented medical condition"
                        checked={data.Incapable["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormCheckbox
                        name="RequirementsNotMet"
                        label="Did not meet state requirements for extended commitment and provided a probation contract"
                        checked={data.RequirementsNotMet["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <div className="d-flex ms-5">
                        <span className="me-2">
                          Total hours of employment this month:
                        </span>
                        <FormInput
                          name="Hours"
                          error={errors.Hours}
                          value={data.Hours["@value"]}
                          onChange={handleChange}
                          identical={compare("Hours")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </li>
                    <li>
                      <FormTextArea
                        name="IndependentLivingProgress"
                        label="PROGRESS TOWARD ACHIEVING INDEPENDENT LIVING MILESTONES:"
                        error={errors.IndependentLivingProgress}
                        value={data.IndependentLivingProgress["@value"]}
                        onChange={handleChange}
                        identical={compare("IndependentLivingProgress")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        rows="5"
                      />
                    </li>
                  </ol>
                </li>
              </ol>
              <br />
              <FormInput
                name="CompletedBy"
                label="NAME AND TITLE OF PERSON COMPLETING FORM:"
                error={errors.CompletedBy}
                value={data.CompletedBy["@value"]}
                onChange={handleChange}
                identical={compare("CompletedBy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="w-100 border-bottom border-dark"
                printClassName="ps-1"
              />
              <div className="d-flex mt-2">
                <div className="me-2">SIGNATURE:</div>
                <div className="flex-fill border-bottom border-dark">
                  {signatures.filter((sig) => sig.signatureType === "Provider")
                    .length > 0 ? (
                    <>
                      {signatures
                        .filter((sig) => sig.signatureType === "Provider")
                        .map((s) => {
                          return (
                            <div key={moment(s.datetime).unix()}>
                              {`${s.name}${
                                s.signatureRole ? ` ${s.signatureRole}` : ""
                              }`}
                            </div>
                          );
                        })}
                    </>
                  ) : null}
                </div>
              </div>
              <FormInput
                name="SupervisorName"
                label="SUPERVISOR'S NAME AND SIGNATURE (IF REQUIRED):"
                error={errors.SupervisorName}
                value={data.SupervisorName["@value"]}
                onChange={handleChange}
                identical={compare("SupervisorName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="w-100 border-bottom border-dark mt-2"
                printClassName="ps-1"
              />
              <div className="d-flex">
                <div className="me-1">DISTRIBUTION:</div>
                <div className="flex-fill">
                  Original—Child's Social Services Worker (case record),{" "}
                  <i>may be emailed</i>
                  <br />
                  Copy—Facility/Provider File (if applicable)
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KY1294B;
